import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/client";

type Lesson = {
  id: string;
  title: string;
  orderIndex: number;
  lessonType: string;
  unlocked?: boolean;
  completed?: boolean;
  hasLessonQuiz?: boolean;
  quizPassed?: boolean;
  submissionStatus?: string;
};

type CourseQuiz = {
  quizId: string;
  lessonId: string;
  lessonTitle: string;
  title: string;
  questionCount: number;
};

export function DashboardCourse() {
  const { slug } = useParams<{ slug: string }>();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [courseTitle, setCourseTitle] = useState("");
  const [passingScore, setPassingScore] = useState(80);
  const [loading, setLoading] = useState(true);
  const [quizMap, setQuizMap] = useState<Record<string, CourseQuiz>>({});

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/courses/${slug}`)
      .then((res) => {
        if (res.data.success) {
          const c = res.data.data;
          setCourseTitle(c?.title || "");
          if (c?.passingScore != null) setPassingScore(Number(c.passingScore));
        }
      })
      .catch(() => {});
    api
      .get(`/courses/${slug}/lessons`)
      .then((res) => {
        if (res.data.success) setLessons(res.data.data || []);
      })
      .catch(() => setLessons([]))
      .finally(() => setLoading(false));
    api
      .get(`/courses/${slug}/quizzes`)
      .then((res) => {
        if (res.data.success) {
          const map: Record<string, CourseQuiz> = {};
          (res.data.data || []).forEach((q: CourseQuiz) => { map[q.lessonId] = q; });
          setQuizMap(map);
        }
      })
      .catch(() => {});
  }, [slug]);

  if (!slug) return null;

  const completedCount = lessons.filter((l) => l.completed).length;
  const allLessonsComplete = lessons.length > 0 && completedCount === lessons.length;

  return (
    <div className="min-h-full bg-stone/40">
      <div className="bg-ink-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Dashboard
          </Link>
          <p className="text-gold-300/90 font-semibold text-xs tracking-[0.2em] uppercase mt-6">
            Course
          </p>
          <h1 className="font-serif text-3xl font-semibold tracking-tight mt-2">
            {courseTitle || "Course"}
          </h1>
          <p className="mt-2 text-slate-400 text-sm">
            {loading
              ? "Loading lessons…"
              : `${lessons.length} lesson${lessons.length !== 1 ? "s" : ""} · ${completedCount}/${lessons.length} complete · Passing score: ${passingScore}%`}
          </p>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" aria-hidden />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="animate-pulse h-16 bg-white rounded-xl border border-gray-200" />
            ))}
          </div>
        ) : (
          <>
            <div className="rounded-2xl border border-gray-200 bg-white shadow-soft overflow-hidden divide-y divide-gray-100">
              {lessons.map((lesson, idx) => {
                const hasQuiz = lesson.hasLessonQuiz ?? !!quizMap[lesson.id];
                const locked = lesson.unlocked === false;
                const subStatus = lesson.submissionStatus || "none";
                const rowClass =
                  "flex items-center gap-4 px-5 py-4 transition-colors group";
                const inner = (
                  <>
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl font-semibold text-sm tabular-nums ${
                        locked
                          ? "bg-gray-100 text-gray-400"
                          : "bg-gold-400/10 text-gold-600 group-hover:bg-gold-400/20"
                      }`}
                    >
                      {locked ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      ) : (
                        idx + 1
                      )}
                    </span>
                    <span
                      className={`font-medium flex-1 min-w-0 ${
                        locked ? "text-gray-500" : "text-ink-950 group-hover:text-gold-600"
                      }`}
                    >
                      {lesson.title}
                    </span>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                      {lesson.completed && (
                        <span className="text-xs font-medium text-green-700 px-2 py-1 rounded-md bg-green-50 border border-green-200">
                          Done
                        </span>
                      )}
                      {hasQuiz && !lesson.completed && subStatus === "pending" && (
                        <span className="text-xs font-medium text-amber-700 px-2 py-1 rounded-md bg-amber-50 border border-amber-200">
                          Awaiting review
                        </span>
                      )}
                      {hasQuiz && !lesson.completed && subStatus === "graded" && !lesson.quizPassed && (
                        <span className="text-xs font-medium text-red-700 px-2 py-1 rounded-md bg-red-50 border border-red-200">
                          Not passed
                        </span>
                      )}
                      {hasQuiz && lesson.completed && (
                        <span className="text-xs font-medium text-emerald-700 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200">
                          Quiz passed
                        </span>
                      )}
                      {hasQuiz && !lesson.completed && subStatus === "none" && (
                        <span className="text-xs font-medium text-blue-600 uppercase tracking-wider px-2 py-1 rounded-md bg-blue-50 border border-blue-200">
                          Quiz
                        </span>
                      )}
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2.5 py-1 rounded-md bg-gray-100">
                        {lesson.lessonType === "video" ? "Video" : "Activity"}
                      </span>
                    </div>
                    {!locked && (
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-gold-500 shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    )}
                  </>
                );
                return locked ? (
                  <div key={lesson.id} className={`${rowClass} bg-gray-50/80 cursor-not-allowed opacity-90`}>
                    {inner}
                  </div>
                ) : (
                  <Link
                    key={lesson.id}
                    to={`/dashboard/courses/${slug}/lessons/${lesson.id}`}
                    className={`${rowClass} hover:bg-stone/40`}
                  >
                    {inner}
                  </Link>
                );
              })}
            </div>

            <div className="mt-10 rounded-2xl border-2 border-ink-950 bg-ink-950 text-white p-6 md:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div>
                  <h2 className="font-serif text-xl font-semibold tracking-tight">
                    Certificate
                  </h2>
                  <p className="mt-1 text-slate-400 text-sm">
                    {allLessonsComplete
                      ? "You've completed every lesson! Your instructor will issue your certificate. Check back soon."
                      : `Complete all ${lessons.length} lessons (and pass each lesson quiz where applicable, ${passingScore}%+) to earn your certificate.`}
                  </p>
                </div>
                {allLessonsComplete && (
                  <Link
                    to={`/dashboard/courses/${slug}/certificate`}
                    className="inline-flex items-center gap-2 rounded-lg bg-gold-400 px-6 py-3.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-md shrink-0"
                  >
                    View certificate
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                )}
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 text-center">
              <p className="text-sm text-gray-600">
                Optional:{" "}
                <Link to={`/dashboard/courses/${slug}/assessment`} className="font-semibold text-gold-600 hover:text-gold-500">
                  Final assessment
                </Link>{" "}
                for extra practice (not required for your certificate).
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
